import { yup } from '@framework/validation/initYup.ts';
import { has } from 'lodash';
import { v4 as uuid } from 'uuid';
import { type Datatype } from '../../expressions/datatype/Datatype.ts';
import { type IBaseWidget } from '../IBaseWidget.ts';
import { type IWidgetType } from '../IWidgetType.ts';
import { type Widget, WidgetType } from '../Widget.ts';

/**
 * Classe che permette di gestire in maniera centralizzata la connessione tra
 * un componente react e il tipo di dato (schema JSON).
 *
 * I componenti sono registrati all'interno della struttura `IWidgetType`
 * nei file `*.type.ts` in cui vengono definite tutte le proprietà necessarie.
 * Forniti i dati di un elemento a livello di `object`, il repository fornirà il
 * componente react necessario per la renderizzazione.
 */
class WidgetRepositoryClass {
  public types: Record<string, IWidgetType<any>> = {};
  public isInitialized = false;

  register<T extends IBaseWidget>(widgetType: IWidgetType<T>) {
    if (has(this.types, widgetType.type)) {
      const error = new Error(
        `Impossibile registrare un Tipo  di Widget con tipo: "${widgetType.type}" poiché è già registrato`,
      );

      if (process.env.NODE_ENV === 'development') {
        console.warn(error.message);
        return;
      }

      throw error;
    }

    // Sulla base di queste variabili di ambiente, si decide se registrare o meno il widget
    // Se la variabile di ambiente `ENABLE_WIDGET_SELECTION` è impostata e il tipo di widget NON è incluso nell'elenco in WIDGET_SELECTION viene skippato
    // Se la variabile di ambiente `ENABLE_WIDGET_SELECTION` NON è impostata, il widget viene registrato
    // Se la variabile di ambiente `ENABLE_WIDGET_SELECTION` è impostata e il tipo di widget è incluso nell'elenco in WIDGET_SELECTION, viene registrato
    if (
      process.env.ENABLE_WIDGET_SELECTION === 'true' &&
      !process.env.WIDGET_SELECTION?.trim()
        .toLowerCase()
        .split(',')
        .includes(widgetType.type.toLowerCase())
    ) {
      return;
    }

    this.types[widgetType.type] = widgetType;
  }

  find<T extends IBaseWidget = Widget>(code: string | null | undefined) {
    if (code == null) return null;
    if (code === WidgetType.Root) return null;

    if (!has(this.types, code)) {
      // biome-ignore format: <tbd>
      console.error(`Impossibile ottenere il Tipo di Widget con codice: "${code}" poiché non è registrato`);
    }

    return this.types[code] as IWidgetType<T> | null;
  }

  listAll() {
    return Object.values(this.types);
  }

  getConfigValidationSchema(
    widget: Widget | null | undefined,
    defaultSchema: any = yup.object(),
  ) {
    if (!widget) return null;
    const item = this.find(widget.type);

    const fieldsValidator =
      item?.configValidator ??
      (defaultSchema as yup.ObjectSchema<yup.AnyObject>);

    // TODO Perché qui aggiungiamo `noUnknown` se invece in `LazyWidgetSchema` non lo aggiungiamo? @Khaos101
    const result = fieldsValidator.noUnknown().meta({ widget: widget.type });

    return result as yup.ObjectSchema<Widget>;
  }

  getConfigComponent(widget: Widget) {
    const item = this.find(widget.type);
    if (!item?.configComponent) {
      // biome-ignore format: <tbd>
      console.warn(`[widgetConfigEditor] Nessun componente di configurazioneper il widget ${widget.type}`, {widget});
      // biome-ignore format: <tbd>
      throw new Error(`Nessuno componente di validazione per il widget ${widget.type}`);
    }
    return item.configComponent;
  }

  getInitial(
    widgetType: WidgetType,
    partialWidget?: Partial<Widget>,
  ): Widget | null {
    const widget = this.find(widgetType);

    if (!widget) {
      // biome-ignore format: <tbd>
      console.warn(`[widgetConfigEditor] Nessun componente di configurazione per il widget ${widgetType}`, {widgetType});
      return null;
    }
    const result: Widget | null =
      (widget.configValidator?.cast(
        {
          // Imposto l'UUID del widget appena creato
          id: uuid(),
          // Imposto una label/titolo di default (i campi verrano rimossi se non necessari)
          label: [{ type: 'text', text: `Nuovo elemento ${widget.name}` }],
          title: [{ type: 'text', text: `Nuovo elemento ${widget.name}` }],
          // Passo tutti i valori di default forniti alla funzione
          ...partialWidget,
        },
        { assert: false },
      ) as Widget | undefined) ?? null;

    return result;
  }

  /**
   * Ritorna il tipo di dato del widget fornito.
   */
  getFieldDataType(widgetType: IWidgetType<any>, widget: IBaseWidget) {
    const field = widgetType.getReference?.([], widget);
    return field?.field.type;
  }

  /**
   * Mappa precalcolata WidgetType → Datatype.
   * Viene costruita lazily alla prima chiamata di `getWidgetDataTypeMap()`.
   */
  private _widgetDataTypeMap: Record<string, Datatype> | null = null;

  /**
   * Ritorna la mappa WidgetType -> Datatype.
   * Se non è già stata costruita, la costruisce iterando su tutti i widget registrati.
   */
  getWidgetDataTypeMap(): Record<string, Datatype> {
    if (this._widgetDataTypeMap) return this._widgetDataTypeMap;

    const map: Record<string, Datatype> = {};
    for (const widgetType of this.listAll()) {
      const dummyWidget = this.getInitial(widgetType.type as WidgetType);
      if (!dummyWidget) continue;

      const dataType = this.getFieldDataType(widgetType, dummyWidget);
      if (dataType) {
        map[widgetType.type] = dataType;
      }
    }

    this._widgetDataTypeMap = map;
    return map;
  }

  /**
   * Verifica se un tipo di widget è compatibile con un Datatype,
   * confrontando il Datatype atteso con quello dichiarato dalla reference del widget.
   */
  isWidgetTypeCompatible(
    expectedDataType: Datatype,
    widgetType: WidgetType,
  ): boolean {
    const map = this.getWidgetDataTypeMap();
    const widgetDataType = map[widgetType];
    if (!widgetDataType) return true; // widget senza reference
    return widgetDataType === expectedDataType;
  }
}

/**
 * L'inizializzazione dei tipi di Widget avviene in fase di inizializzazione
 * in `initWidgetRepository.ts`.
 * Questo script viene chiamato:
 * - In fase di inizializzazione dell'applicazione, quando viene importato
 *   `@framework/engine`. In questo modo i tipi di widget sono disponibili
 * - Per i test Jest, in `initJestWidgetRepository.ts`
 */
export const WidgetRepository = new WidgetRepositoryClass();
